"use client";

import React from "react";
import { CircleAlert, CircleCheck } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const portfolioItems = [{
  id: 1,
  title: "Désencombrement de sous-sol",
  description: "Un sous-sol d'Orléans encombré de vieux meubles et débris de construction. Nous avons tout évacué en une matinée pour libérer l'espace.",
  beforeImage: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/orleans_basement_junk_removal_before.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/orleans_basement_junk_removal_before.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/orleans_basement_junk_removal_before.png-wvc-sizes",
    alt: "Sous-sol encombré à Orléans avant le nettoyage"
  },
  afterImage: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/orleans_basement_junk_removal_after.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/orleans_basement_junk_removal_after.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/orleans_basement_junk_removal_after.png-wvc-sizes",
    alt: "Sous-sol d'Orléans propre et vide après l'intervention"
  }
}, {
  id: 2,
  title: "Nettoyage de garage complet",
  description: "Accumulation d'objets hétéroclites empêchant de stationner le véhicule. Tri sélectif et élimination responsable des rebuts.",
  beforeImage: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/garage_cleanup_orleans_before.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/garage_cleanup_orleans_before.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/garage_cleanup_orleans_before.png-wvc-sizes",
    alt: "Garage rempli de déchets et boîtes"
  },
  afterImage: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/garage_cleanup_orleans_after.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/garage_cleanup_orleans_after.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/garage_cleanup_orleans_after.png-wvc-sizes",
    alt: "Garage organisé et dégagé"
  }
}, {
  id: 3,
  title: "Évacuation de débris de jardin",
  description: "Branches, vieilles clôtures et résidus de paysagement qui s'accumulaient. Transformation radicale de la cour arrière.",
  beforeImage: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/yard_waste_removal_before.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/yard_waste_removal_before.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/yard_waste_removal_before.png-wvc-sizes",
    alt: "Cour arrière avec débris de bois et déchets"
  },
  afterImage: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/yard_waste_removal_after.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/yard_waste_removal_after.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/yard_waste_removal_after.png-wvc-sizes",
    alt: "Cour arrière propre et sécuritaire"
  }
}, {
  id: 4,
  title: "Vidage de grenier résidentiel",
  description: "Des décennies d'objets inutilisés stockés sous les combles. Évacuation sécuritaire respectant l'intégrité de la structure.",
  beforeImage: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/attic_junk_clearance_before.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/attic_junk_clearance_before.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/attic_junk_clearance_before.png-wvc-sizes",
    alt: "Grenier sombre rempli de vieilleries"
  },
  afterImage: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/attic_junk_clearance_after.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/attic_junk_clearance_after.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/attic_junk_clearance_after.png-wvc-sizes",
    alt: "Grenier spacieux et totalement vidé"
  }
}];
export default function BeforeAfterGallery() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="254164" id="before-after-gallery" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground mb-4 tracking-tight">
            Galerie Avant & Après
          </h2>
          <p className="text-muted-foreground text-xl font-default">
            Découvrez l'efficacité de nos services de collecte de rebuts à Orléans. Voyez comment nous transformons vos espaces encombrés en lieux de vie sains et dégagés.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {portfolioItems.map((item, index) => {
          const {
            ref: cardRef,
            isVisible: isCardVisible
          } = useRevealOnScroll({
            threshold: 0.1
          });
          return <div key={item.id} data-index={index} ref={cardRef} className={`transition-all duration-700 ease-out ${isCardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${index * 150}ms`
          }}>
                <Card className="overflow-hidden h-full flex flex-col shadow-lg hover:shadow-xl transition-shadow duration-300 border-border rounded-lg">
                  <CardContent className="p-0 grid grid-cols-2 relative">
                    {}
                    <div className="absolute inset-y-0 left-1/2 w-px bg-background z-10 transform -translate-x-1/2" />
                    
                    {}
                    <div className="relative group">
                      <AspectRatio ratio={4 / 3}>
                        <img src={item.beforeImage.src} data-wvc-srcset={item.beforeImage.srcset} data-wvc-sizes={item.beforeImage.sizes} alt={item.beforeImage.alt} className="object-cover w-full h-full filter grayscale-[40%] group-hover:grayscale-0 transition-all duration-500" loading="lazy" />
                      </AspectRatio>
                      <Badge variant="destructive" className="absolute top-3 left-3 shadow-md bg-destructive/90 backdrop-blur-sm rounded-md">
                        <CircleAlert className="w-3 h-3 mr-1" />
                        Avant
                      </Badge>
                    </div>

                    {}
                    <div className="relative group">
                      <AspectRatio ratio={4 / 3}>
                        <img src={item.afterImage.src} data-wvc-srcset={item.afterImage.srcset} data-wvc-sizes={item.afterImage.sizes} alt={item.afterImage.alt} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                      </AspectRatio>
                      <Badge variant="default" className="absolute top-3 right-3 shadow-md bg-primary/90 backdrop-blur-sm rounded-md">
                        <CircleCheck className="w-3 h-3 mr-1" />
                        Après
                      </Badge>
                    </div>
                  </CardContent>
                  
                  <CardHeader className="flex-grow bg-card">
                    <CardTitle className="font-default text-2xl text-foreground font-semibold">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-lg mt-2 font-default">
                      {item.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}